/**
 * Proyecto: MOGOTAX-SERVER
 * Autor: Megatrack GPS SAS
 * Fecha de creación: 11 de noviembre 2024
 * Licencia: Propiedad Privada
 *
 * Este código es propiedad exclusiva de Megatrack GPS SAS & Mogotax SAS y no puede ser distribuido, copiado, modificado, publicado, sublicenciado ni comercializado sin el consentimiento expreso y por escrito de ambas empresa.
 *
 * Copyright (c) 2025 Megatrack GPS SAS. Todos los derechos reservados.
 */
import React from "react";
import PropTypes from "prop-types";
import { Layout, Drawer } from "antd";
import {
  TeamOutlined,
  LogoutOutlined,
  SettingOutlined,
  UserOutlined,
  LockOutlined,
  CalendarOutlined,
  DashboardOutlined,
  CarOutlined,
  CloseOutlined,
  EditOutlined,
  FileTextOutlined,
  SwapOutlined,
} from "@ant-design/icons";
import { useNavigate, useLocation } from "react-router-dom";
import { 
  Box, 
  Typography, 
  useTheme, 
  useMediaQuery,
  Avatar,
  Chip,
  keyframes,
  IconButton,
  Fade
} from "@mui/material";
import { styled } from '@mui/material/styles';
import logo from "../Assets/img/logo.webp";
import { useUser } from "../UserContext";
import { useSettings } from "../SettingsContext";
import { message as antMessage } from "antd";
import { esAdministrador, otrosPerfiles } from "../utils/perfil";

const { Sider } = Layout;

// Animaciones
const pulseAnimation = keyframes`
  0%, 100% { 
    transform: scale(1);
    opacity: 0.8;
  }
  50% { 
    transform: scale(1.05);
    opacity: 1;
  }
`;

const slideInAnimation = keyframes`
  from {
    opacity: 0;
    transform: translateX(-20px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
`;

const glowAnimation = keyframes`
  0%, 100% { 
    box-shadow: 0 0 5px rgba(10, 46, 92, 0.5);
  }
  50% { 
    box-shadow: 0 0 20px rgba(10, 46, 92, 0.8), 0 0 30px rgba(10, 46, 92, 0.4);
  }
`;

const scanLineAnimation = keyframes`
  0% { left: -100%; }
  100% { left: 100%; }
`;

// Contenedor principal del sidebar
const SidebarContainer = styled(Box)(({ theme, isMobile }) => ({
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
  maxHeight: '100vh',
  background: 'linear-gradient(180deg, #0a2e5c 0%, #051b38 50%, #001529 100%)',
  position: 'relative',
  overflow: 'hidden',
  
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: `
      radial-gradient(circle at 20% 20%, rgba(10, 46, 92, 0.4) 0%, transparent 50%),
      radial-gradient(circle at 80% 80%, rgba(10, 46, 92, 0.3) 0%, transparent 50%),
      linear-gradient(45deg, transparent 30%, rgba(10, 46, 92, 0.1) 50%, transparent 70%)
    `,
    zIndex: 1,
  },
  
  '&::after': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: '-100%',
    width: '100%',
    height: '2px',
    background: 'linear-gradient(90deg, transparent, rgba(10, 46, 92, 0.8), transparent)',
    animation: `${scanLineAnimation} 4s linear infinite`,
    zIndex: 2,
  },
}));

// Logo con efectos
const StyledLogo = styled('img')(({ theme, isMobile }) => ({
  width: isMobile ? '120px' : '140px',
  height: 'auto',
  borderRadius: 12,
  filter: 'drop-shadow(0 0 15px rgba(10, 46, 92, 0.8))',
  animation: `${pulseAnimation} 4s ease-in-out infinite`,
  transition: 'all 0.3s ease',
  zIndex: 10,
  position: 'relative',
  
  '&:hover': {
    transform: 'scale(1.05)',
    filter: 'drop-shadow(0 0 25px rgba(10, 46, 92, 1))',
  },
}));

// Tarjeta de perfil de conductor
const DriverCard = styled(Box)(({ theme, isMobile }) => ({
  background: 'rgba(255, 255, 255, 0.05)',
  backdropFilter: 'blur(10px)',
  border: '1px solid rgba(10, 46, 92, 0.3)',
  borderRadius: 16,
  padding: theme.spacing(2),
  margin: theme.spacing(1.5, 0),
  position: 'relative',
  zIndex: 10,
  overflow: 'hidden',
  animation: `${slideInAnimation} 0.8s ease-out`,
  
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: '-100%',
    width: '100%',
    height: '100%',
    background: 'linear-gradient(90deg, transparent, rgba(10, 46, 92, 0.1), transparent)',
    animation: `${scanLineAnimation} 6s linear infinite`,
    zIndex: 0,
  },
  
  '& *': {
    position: 'relative',
    zIndex: 1,
  },
}));

// Elemento de menú personalizado
const MenuItem = styled(Box)(({ theme, active, isMobile }) => ({
  display: 'flex',
  alignItems: 'center',
  padding: theme.spacing(1.5, 2),
  margin: theme.spacing(0.5, 1),
  borderRadius: 12,
  cursor: 'pointer',
  transition: 'all 0.3s ease',
  position: 'relative',
  zIndex: 10,
  overflow: 'hidden',
  color: active ? '#ffffff' : 'rgba(255, 255, 255, 0.8)',
  background: active ? 'rgba(10, 46, 92, 0.6)' : 'transparent',
  border: active ? '1px solid rgba(10, 46, 92, 0.8)' : '1px solid transparent',
  
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: active ? 0 : '-100%',
    width: '100%',
    height: '100%',
    background: 'linear-gradient(90deg, transparent, rgba(10, 46, 92, 0.3), transparent)',
    transition: 'left 0.3s ease',
    zIndex: 0,
  },
  
  '&:hover': {
    background: 'rgba(10, 46, 92, 0.4)',
    border: '1px solid rgba(10, 46, 92, 0.6)',
    color: '#ffffff',
    transform: 'translateX(8px)',
    boxShadow: '0 4px 15px rgba(10, 46, 92, 0.3)',
    
    '&::before': {
      left: 0,
    },
  },
  
  '& .menu-icon': {
    fontSize: isMobile ? '20px' : '22px',
    marginRight: theme.spacing(1.5),
    filter: 'drop-shadow(0 0 5px rgba(10, 46, 92, 0.8))',
    transition: 'all 0.3s ease',
    zIndex: 1,
    position: 'relative',
  },
  
  '& .menu-text': {
    fontSize: isMobile ? '0.9rem' : '1rem',
    fontWeight: active ? 600 : 400,
    letterSpacing: '0.5px',
    zIndex: 1,
    position: 'relative',
  },
  
  '&:hover .menu-icon': {
    transform: 'scale(1.2)',
    filter: 'drop-shadow(0 0 10px rgba(10, 46, 92, 1))',
  },
  
  ...(active && {
    animation: `${glowAnimation} 3s ease-in-out infinite`,
  }),
}));

// Botón de acción (logout, cambiar contraseña)
const ActionButton = styled(Box)(({ theme, danger, isMobile }) => ({
  display: 'flex',
  alignItems: 'center',
  padding: theme.spacing(1.5, 2),
  margin: theme.spacing(0.5, 1),
  borderRadius: 12,
  cursor: 'pointer',
  transition: 'all 0.3s ease',
  position: 'relative',
  zIndex: 10,
  overflow: 'hidden',
  color: danger ? '#ff6b6b' : 'rgba(255, 255, 255, 0.8)',
  border: danger ? '1px solid rgba(255, 107, 107, 0.3)' : '1px solid rgba(255, 255, 255, 0.2)',
  
  '&:hover': {
    background: danger ? 'rgba(255, 107, 107, 0.1)' : 'rgba(255, 255, 255, 0.1)',
    border: danger ? '1px solid rgba(255, 107, 107, 0.6)' : '1px solid rgba(255, 255, 255, 0.4)',
    color: danger ? '#ff8a80' : '#ffffff',
    transform: 'translateX(8px)',
    boxShadow: danger ? '0 4px 15px rgba(255, 107, 107, 0.3)' : '0 4px 15px rgba(255, 255, 255, 0.2)',
  },
  
  '& .action-icon': {
    fontSize: isMobile ? '18px' : '20px',
    marginRight: theme.spacing(1.5),
    filter: danger ? 'drop-shadow(0 0 5px rgba(255, 107, 107, 0.8))' : 'drop-shadow(0 0 5px rgba(255, 255, 255, 0.5))',
    transition: 'all 0.3s ease',
  },
  
  '& .action-text': {
    fontSize: isMobile ? '0.85rem' : '0.95rem',
    fontWeight: 500,
    letterSpacing: '0.5px',
  },
  
  '&:hover .action-icon': {
    transform: 'scale(1.1)',
  },
}));

// Chip de estado/vehículo
const StatusChip = styled(Chip)(({ theme, status }) => ({
  backgroundColor: status === 'online' ? 'rgba(76, 175, 80, 0.2)' : 'rgba(255, 152, 0, 0.2)',
  color: status === 'online' ? '#4caf50' : '#ff9800',
  border: status === 'online' ? '1px solid rgba(76, 175, 80, 0.5)' : '1px solid rgba(255, 152, 0, 0.5)',
  fontSize: '0.75rem',
  fontWeight: 600,
  animation: `${pulseAnimation} 3s ease-in-out infinite`,
  
  '& .MuiChip-icon': {
    color: 'inherit',
    fontSize: '16px',
  },
}));

// Contenedor de versión
const VersionContainer = styled(Box)(({ theme }) => ({
  textAlign: 'center',
  padding: theme.spacing(1),
  borderTop: '1px solid rgba(10, 46, 92, 0.3)',
  background: 'rgba(0, 0, 0, 0.2)',
  position: 'relative',
  zIndex: 10,
  
  '& .version-text': {
    color: 'rgba(255, 255, 255, 0.6)',
    fontSize: '0.7rem',
    fontWeight: 300,
    letterSpacing: '1px',
    textTransform: 'uppercase',
  },
}));

// Header del drawer móvil
const DrawerHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: theme.spacing(0.75, 1.5),
  background: 'rgba(10, 46, 92, 0.8)',
  borderBottom: '1px solid rgba(10, 46, 92, 0.3)',
  position: 'relative',
  zIndex: 10,
  minHeight: '48px',
  
  '& .header-title': {
    color: '#ffffff',
    fontSize: '0.95rem',
    fontWeight: 600,
    letterSpacing: '0.5px',
    textTransform: 'uppercase',
  },
}));

function Sidebar({ open, toggleDrawer }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, setUser, logoutUser, cambiarPerfil } = useUser();
  const { isPreoperationalActive } = useSettings();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const version = "1.0.6";
  const administrador = esAdministrador(user);
  const perfilesParaCambiar = otrosPerfiles(user);

  const handleListItemClick = (route) => {
    // Bloquear navegación si el formulario preoperacional está activo
    if (isPreoperationalActive) {
      antMessage.warning({
        content: 'Debes completar el formulario preoperacional antes de continuar',
        duration: 4,
      });
      return;
    }

    navigate(`/modules/${route}`);
    if (isMobile) {
      toggleDrawer();
    }
  };

  const handleLogout = () => {
    // Bloquear logout si el formulario preoperacional está activo
    if (isPreoperationalActive) {
      antMessage.warning({
        content: 'Debes completar el formulario preoperacional antes de cerrar sesión',
        duration: 4,
      });
      return;
    }

    if (administrador) {
      // Cierra la sesión también en el servidor y limpia todo
      logoutUser().finally(() => navigate("/login"));
      return;
    }

    setUser(null);
    localStorage.removeItem("user");
    localStorage.removeItem("accessToken");
    localStorage.removeItem("refreshToken");
    navigate("/login");
  };

  // Cambio de perfil de la misma persona (conductor ↔ administrador de vehículos), sin contraseña
  const handleCambiarPerfil = async (perfil) => {
    if (isPreoperationalActive) {
      antMessage.warning({
        content: 'Debes completar el formulario preoperacional antes de cambiar de perfil',
        duration: 4,
      });
      return;
    }

    try {
      const data = await cambiarPerfil(perfil.perfil);
      if (isMobile) {
        toggleDrawer();
      }
      antMessage.success(`Ingresaste como ${perfil.etiqueta}`);
      if (data.requiere_cambio_password) {
        navigate("/change-password?mandatory=true");
      } else if (esAdministrador(data.user)) {
        navigate("/modules/Dashboard");
      } else {
        // Como al iniciar sesión: el formulario decide si aplica el preoperacional
        navigate("/modules/PreOperationalForm");
      }
    } catch (error) {
      antMessage.error(error.response?.data?.message || 'No se pudo cambiar de perfil');
    }
  };

  const currentRoute = location.pathname.split("/")[2];

  const menuItems = administrador ? [
    {
      label: "Traslados de mis vehículos",
      key: "Dashboard",
      icon: <TeamOutlined className="menu-icon" />,
      onClick: () => handleListItemClick("Dashboard"),
    },
  ] : [
    {
      label: "Mis Traslados",
      key: "Dashboard",
      icon: <TeamOutlined className="menu-icon" />,
      onClick: () => handleListItemClick("Dashboard"),
    },
    {
      label: "PreOperacionales",
      key: "PreOperations",
      icon: <CalendarOutlined className="menu-icon" />,
      onClick: () => handleListItemClick("PreOperations"),
    },
    {
      label: "Mi Perfil",
      key: "Profile",
      icon: <UserOutlined className="menu-icon" />,
      onClick: () => handleListItemClick("Profile"),
    },
    {
      label: "Mi Firma",
      key: "Firma",
      icon: <EditOutlined className="menu-icon" />,
      onClick: () => handleListItemClick("Firma"),
    },
    {
      label: "Mis FUECs",
      key: "fuecs",
      icon: <FileTextOutlined className="menu-icon" />,
      onClick: () => handleListItemClick("fuecs"),
    },
    {
      label: "Configuración",
      key: "Settings",
      icon: <SettingOutlined className="menu-icon" />,
      onClick: () => handleListItemClick("Settings"),
    },
  ];

  const getVehicleInfo = () => {
    if (administrador) {
      const placas = (user?.vehiculos || []).map((v) => v.placa || v.codigo).filter(Boolean);
      if (placas.length === 0) return "Sin vehículos";
      return placas.length <= 3 ? placas.join(" · ") : `${placas.length} vehículos`;
    }
    if (user && user.vehiculoPrincipal) {
      // Buscar en múltiples ubicaciones para compatibilidad
      const v = user.vehiculoPrincipal.Vehiculo || user.vehiculoPrincipal.vehiculo || user.vehiculoPrincipal;
      if (v && v.placa) {
        return v.placa;
      }
      if (v && v.modelo) {
        return v.modelo;
      }
    }
    return "Sin asignar";
  };

  const sidebarContent = (
    <SidebarContainer isMobile={isMobile}>
      {/* Header fijo: Logo */}
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        p: isMobile ? 1.5 : 2,
        position: 'relative',
        zIndex: 10,
        flexShrink: 0
      }}>
        <StyledLogo src={logo} alt="Mogotax Logo" isMobile={isMobile} />
      </Box>

      {/* Área scrolleable central */}
      <Box sx={{ 
        flex: 1, 
        overflowY: 'auto',
        overflowX: 'hidden',
        position: 'relative',
        zIndex: 10,
        '&::-webkit-scrollbar': {
          width: '4px',
        },
        '&::-webkit-scrollbar-track': {
          background: 'rgba(10, 46, 92, 0.1)',
        },
        '&::-webkit-scrollbar-thumb': {
          background: 'rgba(10, 46, 92, 0.5)',
          borderRadius: '4px',
        },
        '&::-webkit-scrollbar-thumb:hover': {
          background: 'rgba(10, 46, 92, 0.7)',
        },
      }}>
        {/* Información del Conductor */}
        <Box sx={{ px: 2, pb: 1 }}>
          <DriverCard isMobile={isMobile}>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 1 }}>
              <Avatar 
                sx={{ 
                  width: isMobile ? 45 : 55, 
                  height: isMobile ? 45 : 55, 
                  mb: 1,
                  background: 'linear-gradient(135deg, #0a2e5c, #051b38)',
                  border: '2px solid rgba(10, 46, 92, 0.5)',
                  fontSize: isMobile ? '1.1rem' : '1.3rem',
                  fontWeight: 'bold'
                }}
              >
                {user?.nombre?.charAt(0)?.toUpperCase() || 'C'}
              </Avatar>
              <Typography 
                variant={isMobile ? "body1" : "subtitle1"} 
                sx={{ 
                  fontWeight: 700, 
                  color: '#ffffff',
                  textAlign: 'center',
                  letterSpacing: '0.5px',
                  fontSize: isMobile ? '0.9rem' : '1rem'
                }}
              >
                {user?.nombre || "Conductor"}
              </Typography>
              {administrador && (
                <Typography
                  variant="caption"
                  sx={{ color: 'rgba(255, 255, 255, 0.75)', textAlign: 'center' }}
                >
                  Administrador de vehículos · Solo lectura
                </Typography>
              )}
            </Box>
            
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, mb: 1 }}>
              <CarOutlined style={{ color: '#0a2e5c', fontSize: '14px' }} />
              <Typography 
                variant="body2" 
                sx={{ 
                  color: 'rgba(255, 255, 255, 0.9)',
                  fontWeight: 500,
                  fontSize: isMobile ? '0.75rem' : '0.85rem'
                }}
              >
                {getVehicleInfo()}
              </Typography>
            </Box>
            
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1 }}>
              <StatusChip 
                icon={<DashboardOutlined />}
                label="En línea" 
                size="small" 
                status="online"
              />
            </Box>
          </DriverCard>
        </Box>

        {/* Menú de navegación */}
        <Box sx={{ px: 1, py: 1 }}>
          {menuItems.map((item) => (
            <Fade key={item.key} in={true} timeout={300 + menuItems.indexOf(item) * 100}>
              <div>
                <MenuItem
                  active={currentRoute === item.key}
                  isMobile={isMobile}
                  onClick={item.onClick}
                >
                  {item.icon}
                  <Typography className="menu-text">
                    {item.label}
                  </Typography>
                </MenuItem>
              </div>
            </Fade>
          ))}
        </Box>

        {/* Acciones */}
        <Box sx={{ px: 2, py: 1, mt: 1 }}>
          {perfilesParaCambiar.map((perfil) => (
            <ActionButton
              key={perfil.perfil}
              onClick={() => handleCambiarPerfil(perfil)}
              isMobile={isMobile}
            >
              <SwapOutlined className="action-icon" />
              <Typography className="action-text">
                Cambiar a {perfil.etiqueta}
              </Typography>
            </ActionButton>
          ))}

          <ActionButton onClick={() => handleListItemClick("ChangePassword")} isMobile={isMobile}>
            <LockOutlined className="action-icon" />
            <Typography className="action-text">
              Cambiar Contraseña
            </Typography>
          </ActionButton>

          <ActionButton onClick={handleLogout} danger isMobile={isMobile}>
            <LogoutOutlined className="action-icon" />
            <Typography className="action-text">
              Cerrar Sesión
            </Typography>
          </ActionButton>
        </Box>
      </Box>

      {/* Footer fijo: Versión */}
      <Box sx={{ flexShrink: 0, position: 'relative', zIndex: 10 }}>
        <VersionContainer>
          <Typography className="version-text">
            Versión {version}
          </Typography>
        </VersionContainer>
      </Box>
    </SidebarContainer>
  );

  return isMobile ? (
    <Drawer
      placement="left"
      onClose={toggleDrawer}
      open={open}
      styles={{
        body: { padding: 0 },
        header: { padding: 0 }
      }}
      title={
        <DrawerHeader>
          <Typography className="header-title">
            Menú Principal
          </Typography>
          <IconButton 
            onClick={toggleDrawer}
            sx={{ 
              color: '#ffffff',
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.1)'
              }
            }}
          >
            <CloseOutlined />
          </IconButton>
        </DrawerHeader>
      }
      closeIcon={null}
    >
      {sidebarContent}
    </Drawer>
  ) : (
    <Sider
      width={280}
      collapsible
      trigger={null}
      collapsed={!open}
      collapsedWidth={0}
      style={{
        overflow: "auto",
        height: "100vh",
        position: "fixed",
        left: 0,
        top: 0,
        bottom: 0,
        transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
        boxShadow: "4px 0 20px rgba(0, 0, 0, 0.3)",
        zIndex: 1000,
      }}
    >
      {sidebarContent}
    </Sider>
  );
}

Sidebar.propTypes = {
  open: PropTypes.bool.isRequired,
  toggleDrawer: PropTypes.func.isRequired,
};

export default Sidebar;
